import { Button } from "@legal-500/legal500-design-system";
import {
  AsyncSearchableSelect,
  type AsyncSelectOption,
} from "../../../../components/Form/AsyncSearchableSelect/AsyncSearchableSelect";
import { researcherOptionMatchesSearch, type LabelledResearcher } from "../utils/researcherOption";
import { assigneeOptions, UNASSIGNED_OPTION } from "../utils/assigneeOptions";
import type { AssignmentField } from "../utils/applyBulkAssignment";
import styles from "./BulkAssignBar.module.css";

type BulkAssignBarProps = {
  selectedCount: number;
  researchers: LabelledResearcher[];
  onAssign: (field: AssignmentField, value: string | null) => void;
  onClear: () => void;
  /**
   * Whether the bar is the visible, interactive state of the reveal. The component always mounts
   * while row selection is possible — the CSS grid-row reveal transition needs a persistent node to
   * animate — so `open` (not mounting) is what gates its accessibility: no region role, no name, no
   * focus reachability, while it sits collapsed in a zero-height box.
   */
  open: boolean;
};

/**
 * Applies a researcher or editor to every selected row in the form only — it never sends a request.
 * Saving the change is the Save button's job, later. No status action lives here: bulk assignment
 * never touches a row's status.
 */
export function BulkAssignBar({
  selectedCount,
  researchers,
  onAssign,
  onClear,
  open,
}: BulkAssignBarProps) {
  const options: AsyncSelectOption[] = [UNASSIGNED_OPTION, ...assigneeOptions(researchers)];

  const search = (query: string) => {
    const term = query.trim().toLowerCase();
    return Promise.resolve(
      term ? options.filter(option => researcherOptionMatchesSearch(option, term)) : options,
    );
  };

  const handleAssign = (field: AssignmentField) => (value: string | number | null) => {
    onAssign(field, value === null ? null : String(value));
  };

  return (
    <div
      className={styles.bar}
      role={open ? "region" : undefined}
      aria-label={open ? "Bulk assign selected practice areas" : undefined}
      aria-hidden={!open}
      inert={!open}
    >
      <span className={styles.count}>{open ? `${selectedCount} selected` : ""}</span>
      <div className={styles.picker}>
        <AsyncSearchableSelect
          name="bulk-assign-researcher"
          label=""
          value={null}
          placeholder="Assign researcher"
          onChange={handleAssign("assignedResearcherId")}
          onSearch={search}
          minSearchLength={0}
        />
      </div>
      <div className={styles.picker}>
        <AsyncSearchableSelect
          name="bulk-assign-editor"
          label=""
          value={null}
          placeholder="Assign editor"
          onChange={handleAssign("assignedEditorId")}
          onSearch={search}
          minSearchLength={0}
        />
      </div>
      <Button type="button" variant="text" onClick={onClear}>
        Clear selection
      </Button>
    </div>
  );
}
